import {funeralAllowed,refreshFuneralPolicy} from './welch-funeral-notice.js';
/** Local calendar dates: no UTC conversion or timezone-dependent date shifts. */
export function parseCardDate(value) {
  const match = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(value.trim());
  if (!match) return null;
  const [, day, month, year] = match.map(Number);
  if (year < 1900 || year > 9999) return null;
  const date = new Date(year, month - 1, day, 12);
  return date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day ? date : null;
}
export function formatCardDate(date) {
  return `${String(date.getDate()).padStart(2, '0')}/${String(date.getMonth() + 1).padStart(2, '0')}/${date.getFullYear()}`;
}
export function funeralToday(now = new Date()) {
  const parts = new Intl.DateTimeFormat('en-GB', { timeZone: 'Europe/London', year: 'numeric', month: 'numeric', day: 'numeric' }).formatToParts(now);
  const value = type => Number(parts.find(part => part.type === type).value);
  return new Date(value('year'), value('month') - 1, value('day'), 12);
}
export function isPastFuneralDate(date, now = new Date()) {
  return date < funeralToday(now);
}
export function nativeDateValue(date) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}
export function parseNativeDate(value) {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  return match ? parseCardDate(`${match[3]}/${match[2]}/${match[1]}`) : null;
}
export function useNativeCalendar(device) {
  return /iPad|iPhone|iPod/.test(device.userAgent) || (device.platform === 'MacIntel' && device.maxTouchPoints > 1);
}
class WelchDatePicker extends HTMLElement {
  connectedCallback() {
    this.initialize();
  }
  initialize() {
    this.controller?.abort();
    this.controller = new AbortController();
    const options = { signal: this.controller.signal };
    this.input = this.querySelector('input');
    this.panel = this.querySelector('[data-calendar]');
    this.days = this.querySelector('[data-days]');
    this.monthHeading = this.querySelector('[data-month]');
    this.nativeMode = !this.hasAttribute('data-funeral-notice') && !this.hasAttribute('data-delivery-calendar') && useNativeCalendar(navigator);
    const existing = parseNativeDate(this.input.value) || parseCardDate(this.input.value);
    this.input.type = this.nativeMode ? 'date' : 'text';
    this.input.value = existing ? (this.nativeMode ? nativeDateValue(existing) : formatCardDate(existing)) : '';
    if (this.nativeMode) {
      this.input.removeAttribute('inputmode');
      this.input.removeAttribute('aria-expanded');
      this.input.removeAttribute('aria-controls');
      this.input.min = nativeDateValue(funeralToday());
      this.panel.hidden = true;
      this.input.addEventListener('focus', () => this.validate(), options);
      this.input.addEventListener('input', () => this.validate(), options);
      this.input.addEventListener('change', () => this.validate(), options);
      this.validate();
      return;
    }

    this.input.addEventListener('click', () => this.open(), options);
    this.input.addEventListener('input', () => this.validate(), options);
    this.input.addEventListener('keydown', event => {
      if (event.key === 'ArrowDown') { event.preventDefault(); this.open(true); }
    }, options);
    // Safari can move focus to the surrounding dialog on mouse-down, hiding
    // the calendar before click reaches the day. Keep focus within the picker.
    this.addEventListener('pointerdown', event => {
      const button = event.target.closest('button');
      if (button && !button.disabled && this.contains(button)) { event.preventDefault(); button.focus({preventScroll:true}); }
    }, options);
    this.addEventListener('click', event => {
      const button = event.target.closest('button');
      if (!button || button.disabled || !this.contains(button)) return;
      if (button.hasAttribute('data-prev')) this.moveMonth(-1);
      if (button.hasAttribute('data-next')) this.moveMonth(1);
      if (button.hasAttribute('data-close')) this.close(true);
      if (button.dataset.day) {
        const selected = new Date(this.year, this.month, Number(button.dataset.day), 12);
        if (this.unavailable(selected)) { this.render(); return; }
        this.input.value = formatCardDate(selected);
        this.validate();
        this.input.dispatchEvent(new Event('input', { bubbles: true }));
        this.input.dispatchEvent(new Event('change', { bubbles: true }));
        this.close(true);
      }
    }, options);
    this.addEventListener('keydown', event => {
      if (event.key === 'Escape' && !this.panel.hidden) { event.preventDefault(); event.stopPropagation(); this.close(true); }
      const day = event.target.dataset.day;
      if (!day) return;
      const offsets = { ArrowLeft: -1, ArrowRight: 1, ArrowUp: -7, ArrowDown: 7 };
      if (!(event.key in offsets)) return;
      event.preventDefault();
      const next = new Date(this.year, this.month, Number(day) + offsets[event.key], 12);
      if (this.unavailable(next)) return;
      this.year = next.getFullYear(); this.month = next.getMonth(); this.render();
      this.days.querySelector(`[data-day="${next.getDate()}"]`)?.focus();
    }, options);
    document.addEventListener('pointerdown', event => { if (!this.contains(event.target)) this.close(); }, options);
    this.addEventListener('focusout', event => { if (event.relatedTarget && !this.contains(event.relatedTarget)) this.close(); }, options);
    document.addEventListener('welch:after-section', event => {
      if (event.detail.contains(this)) this.initialize();
    }, options);
    window.addEventListener('pageshow', () => this.initialize(), options);
    this.validate();
  }
  disconnectedCallback() { this.controller?.abort(); }
  unavailable(date) { return (this.hasAttribute('data-funeral-notice') && !funeralAllowed(nativeDateValue(date), this.closest('[data-funeral-details]')?.querySelector('[name="properties[Funeral time]"]')?.value || '18:00')) || isPastFuneralDate(date) || (this.hasAttribute('data-delivery-calendar') && (!this.availableDates || !this.availableDates.has(nativeDateValue(date)))); }
  setAvailability(dates) { this.availableDates=new Set(dates);if(!this.input)return;this.validate();if(!this.panel.hidden)this.render(); }
  validate() {
    if (this.nativeMode) this.input.min = nativeDateValue(funeralToday());
    const date = this.nativeMode ? parseNativeDate(this.input.value) : parseCardDate(this.input.value);
    this.input.setCustomValidity(this.input.value && !date ? 'Please enter a valid date in dd/mm/yyyy format.' : date && this.unavailable(date) ? 'Delivery is unavailable on this date. Please choose an available day.' : '');
  }
  async open(focusDay = false) {
    if(this.hasAttribute('data-funeral-notice'))await refreshFuneralPolicy();
    if (this.input.disabled || this.nativeMode) return;
    this.validate();
    const selected = parseCardDate(this.input.value);
    const date = selected && !isPastFuneralDate(selected) ? selected : funeralToday();
    this.year = date.getFullYear(); this.month = date.getMonth();
    this.render(); this.panel.hidden = false; this.input.setAttribute('aria-expanded', 'true');
    if (focusDay) this.days.querySelector(`[data-day="${date.getDate()}"]`)?.focus();
  }
  close(focusInput = false) {
    this.panel.hidden = true; this.input.setAttribute('aria-expanded', 'false');
    if (focusInput) this.input.focus();
  }
  moveMonth(amount) {
    const date = new Date(this.year, this.month + amount, 1, 12);
    this.year = date.getFullYear(); this.month = date.getMonth(); this.render();
  }
  render() {
    this.monthHeading.textContent = new Intl.DateTimeFormat('en-GB', { month: 'long', year: 'numeric' }).format(new Date(this.year, this.month, 1));
    this.days.replaceChildren();
    const offset = (new Date(this.year, this.month, 1).getDay() + 6) % 7;
    for (let i = 0; i < offset; i++) this.days.append(document.createElement('span'));
    const count = new Date(this.year, this.month + 1, 0).getDate();
    for (let day = 1; day <= count; day++) {
      const date = new Date(this.year, this.month, day, 12);
      const button = document.createElement('button'); button.type = 'button'; button.dataset.day = day; button.textContent = day;
      button.disabled = this.unavailable(date);
      button.setAttribute('aria-label', new Intl.DateTimeFormat('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }).format(date));
      button.setAttribute('aria-pressed', String(formatCardDate(date) === this.input.value));
      if (formatCardDate(date) === formatCardDate(funeralToday())) button.setAttribute('aria-current', 'date');
      this.days.append(button);
    }
  }
}
if (!customElements.get('welch-date-picker')) customElements.define('welch-date-picker', WelchDatePicker);
